import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth, HOME_FOR } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  GraduationCap, Compass, ShieldCheck, Building2, LogOut, LayoutDashboard, Check, ChevronDown, Camera, UserCog, HelpCircle,
} from "lucide-react";

const ROLE_LABEL = {
  student: "Student", advisor: "Academic Advisor", faculty: "Faculty", admin: "Website Administrator", employer: "Employer",
};

const VIEWS = [
  { role: "student", label: "Student View", icon: GraduationCap },
  { role: "advisor", label: "Academic Advisor View", icon: Compass },
  { role: "faculty", label: "Faculty View", icon: Compass },
  { role: "admin", label: "Website Administration", icon: ShieldCheck },
  { role: "employer", label: "Employer View", icon: Building2 },
];

function initials(name = "", email = "") {
  const src = (name || email || "?").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export function UserMenu({ dark = false }) {
  const { user, setUser, logout, switchView, exitPreview } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  if (!user) return null;

  const realRole = user.real_role || user.role;

  const onAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = async () => {
        const max = 256;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        const dataUrl = c.toDataURL("image/jpeg", 0.85);
        try {
          await api.patch("/auth/avatar", { avatar: dataUrl });
          setUser({ ...user, avatar: dataUrl });
          toast.success("Profile photo updated.");
        } catch { toast.error("Couldn't save your photo. Please try again."); }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const doLogout = async () => { await logout(); navigate("/login"); };

  // My Dashboard ALWAYS returns to the user's REAL role and clears any preview.
  const goMyDashboard = async () => {
    if (busy) return;
    if (user.preview) {
      setBusy(true);
      try {
        const u = await exitPreview();
        navigate(HOME_FOR[u.role]);
      } catch { toast.error("Could not return to your dashboard. Please try again."); }
      finally { setBusy(false); }
    } else {
      navigate(HOME_FOR[realRole]);
    }
  };

  const doSwitch = async (role) => {
    if (busy) return;
    // Selecting your real role exits preview back to your authenticated dashboard.
    if (role === realRole) { await goMyDashboard(); return; }
    setBusy(true);
    try {
      const next = await switchView(role);
      toast.success(`Previewing ${ROLE_LABEL[next.preview_role || next.role]} view`);
      navigate(HOME_FOR[next.preview_role || next.role]);
    } catch (e) {
      toast.error("Could not switch preview. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          className={`group flex items-center gap-2 rounded-full py-1 pl-1 pr-2.5 transition-colors ${
            dark ? "hover:bg-white/10" : "hover:bg-[#003057]/8"
          }`}
        >
          {user.avatar
            ? <img src={user.avatar} alt={user.name} className="h-9 w-9 rounded-full object-cover ring-2 ring-white/70" />
            : <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#78be21] text-sm font-extrabold text-[#00243f] ring-2 ring-white/70">{initials(user.name, user.email)}</span>}
          <ChevronDown className={`h-4 w-4 ${dark ? "text-slate-300" : "text-slate-500"}`} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="user-menu-content">
        <div className="px-2 py-2">
          <div className="flex items-center gap-3">
            {user.avatar
              ? <img src={user.avatar} alt={user.name} className="h-11 w-11 rounded-full object-cover" />
              : <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#78be21] text-sm font-extrabold text-[#00243f]">{initials(user.name, user.email)}</span>}
            <div className="min-w-0">
              <p className="truncate text-sm font-bold text-[#003057]" data-testid="user-menu-name">{user.name}</p>
              <p className="truncate text-xs text-slate-500">{user.email}</p>
              {(user.student_number || user.employee_id) && (
                <p className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-slate-400" data-testid="user-menu-id">
                  {user.student_number ? `Student ID: ${user.student_number}` : `Employee ID: ${user.employee_id}`}
                </p>
              )}
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between">
            <span className="inline-block rounded-full bg-[#78be21]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#5fa114]">
              {ROLE_LABEL[realRole]}
            </span>
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="avatar-upload-btn"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#005291] hover:text-[#78be21]">
              <Camera className="h-3.5 w-3.5" /> {user.avatar ? "Change photo" : "Add photo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onAvatar} className="hidden" data-testid="avatar-upload-input" />
          </div>
        </div>
        {user.preview && (
          <div className="mx-2 mb-1 rounded-md bg-amber-50 px-2 py-1.5 text-[11px] font-semibold text-amber-700" data-testid="preview-banner">
            Previewing {ROLE_LABEL[user.preview_role || user.role]}
            {user.preview_persona?.name ? ` · Sample data: ${user.preview_persona.name}` : ""} · Read-only
          </div>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
          Account
        </DropdownMenuLabel>
        <DropdownMenuItem
          data-testid="user-menu-dashboard"
          disabled={busy}
          onClick={goMyDashboard}
          className="cursor-pointer font-semibold text-[#003057]"
        >
          <LayoutDashboard className="mr-2 h-4 w-4" /> My Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="user-menu-profile"
          onClick={() => navigate("/app/profile")}
          className="cursor-pointer font-semibold text-[#003057]"
        >
          <UserCog className="mr-2 h-4 w-4" /> My Profile
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="user-menu-help"
          onClick={() => navigate("/app/help")}
          className="cursor-pointer font-semibold text-[#003057]"
        >
          <HelpCircle className="mr-2 h-4 w-4" /> Help & Documentation
        </DropdownMenuItem>
        {user.can_preview && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Preview as
            </DropdownMenuLabel>
            {VIEWS.map((v) => {
              const active = user.preview ? v.role === user.preview_role : v.role === realRole;
              const isReal = v.role === realRole;
              return (
                <DropdownMenuItem
                  key={v.role}
                  data-testid={`switch-view-${v.role}`}
                  disabled={busy}
                  onClick={() => doSwitch(v.role)}
                  className={`cursor-pointer ${active ? "font-bold text-[#003057]" : "text-slate-600"}`}
                >
                  <v.icon className="mr-2 h-4 w-4" /> {v.label}
                  {isReal && <span className="ml-2 rounded-full bg-slate-100 px-1.5 text-[9px] font-bold uppercase tracking-wide text-slate-400">your role</span>}
                  {active && <Check className="ml-auto h-4 w-4 text-[#78be21]" />}
                </DropdownMenuItem>
              );
            })}
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="user-menu-logout"
          onClick={doLogout}
          className="cursor-pointer font-semibold text-red-600 focus:text-red-600"
        >
          <LogOut className="mr-2 h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
